﻿import { FunctionComponent, useEffect, useMemo } from "react";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Avatar,
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuList,
    Select,
    Spacer,
    Text,
    useDisclosure,
    VStack,
} from "@chakra-ui/react";
import { TenantResponse, useTenants } from "../../api";
import { IconBuildingStore, IconDotsVertical, IconFileSpreadsheet, IconPlus, IconRefresh, IconSearch, IconTrashX, IconX } from "@tabler/icons-react";
import { useUserContext } from "../../context/UserContext";
import { TenantCreatorDialog } from "./TenantCreatorDialog";
import { TenantRestoreDrawer } from "./TenantRestoreDrawer";
import { SetPageTitle } from "../../components/SetPageTitle";
import { formatTimestampStr } from "@am-tax/fe-core";

const avatarColors = ["blue", "teal", "green", "purple", "orange", "pink", "cyan", "red"];
function getAvatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
}

export const AdminClientsPage: FunctionComponent = () => {
    const appInsights = useAppInsightsContext();
    const navigate = useNavigate();
    const user = useUserContext();

    useEffect(() => {
        appInsights.trackPageView({ uri: "/admin/clients" });
    }, [appInsights]);

    const tenantsQuery = useTenants();
    const tenants = tenantsQuery.data ?? [];

    const createDialog = useDisclosure();
    const restoreDrawer = useDisclosure();

    const [searchParams, setSearchParams] = useSearchParams();
    const search = searchParams.get("search") ?? "";
    const statusFilter = (searchParams.get("status") ?? "") as "" | "enabled" | "disabled";

    const setParam = (key: string, value: string) =>
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev);
                value ? next.set(key, value) : next.delete(key);
                return next;
            },
            { replace: true }
        );

    const clearFilters = () =>
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev);
                next.delete("search");
                next.delete("status");
                return next;
            },
            { replace: true }
        );

    const hasActiveFilters = !!(search || statusFilter);

    const filtered = useMemo(
        () =>
            tenants.filter(t => {
                const matchesSearch =
                    !search || t.name.toLowerCase().includes(search.toLowerCase()) || (t.externalId ?? "").toLowerCase().includes(search.toLowerCase());
                const matchesStatus = !statusFilter || (statusFilter === "enabled" ? t.isEnabled : !t.isEnabled);
                return matchesSearch && matchesStatus;
            }),
        [tenants, search, statusFilter]
    );

    return (
        <Box h="100%" overflow="auto" bg="gray.50">
            <SetPageTitle title="System Settings" />

            {/* Page Header */}
            <Flex align="center" px={6} py={4} bg="white" borderBottom="1px solid" borderColor="gray.200">
                <HStack gap={2}>
                    <Text fontSize="xl" fontWeight="semibold">
                        Clients
                    </Text>
                    <Badge colorScheme="gray" variant="subtle" borderRadius="full" px={2}>
                        {tenants.length}
                    </Badge>
                </HStack>
                <Spacer />
                <HStack gap={2}>
                    <Button leftIcon={<Icon as={IconPlus} />} size="sm" colorScheme="blue" onClick={createDialog.onOpen}>
                        Create
                    </Button>
                    <Menu>
                        <MenuButton as={IconButton} icon={<Icon as={IconDotsVertical} />} aria-label="More actions" size="sm" variant="outline" />
                        <MenuList>
                            <MenuItem icon={<Icon as={IconFileSpreadsheet} />}>Export all</MenuItem>
                            <MenuItem icon={<Icon as={IconTrashX} />} onClick={restoreDrawer.onOpen}>
                                Deleted clients
                            </MenuItem>
                            <MenuDivider />
                            <MenuItem
                                icon={<Icon as={IconRefresh} />}
                                onClick={async () => {
                                    await tenantsQuery.refetch();
                                }}
                            >
                                Refresh
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </HStack>
            </Flex>

            <Box px={6} py={4}>
                {/* Toolbar */}
                <HStack mb={3} gap={3}>
                    <InputGroup maxW="360px" size="sm">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={IconSearch} color="gray.400" boxSize={4} />
                        </InputLeftElement>
                        <Input placeholder="Find a client..." value={search} onChange={e => setParam("search", e.target.value)} bg="white" borderRadius="md" />
                    </InputGroup>
                    <Select size="sm" maxW="160px" bg="white" borderRadius="md" value={statusFilter} onChange={e => setParam("status", e.target.value)}>
                        <option value="">All statuses</option>
                        <option value="enabled">Enabled</option>
                        <option value="disabled">Disabled</option>
                    </Select>
                    {hasActiveFilters && (
                        <Button size="sm" variant="ghost" leftIcon={<Icon as={IconX} boxSize={3} />} onClick={clearFilters}>
                            Clear
                        </Button>
                    )}
                    <Spacer />
                    <Text fontSize="sm" color="gray.500">
                        {filtered.length} client{filtered.length !== 1 ? "s" : ""}
                    </Text>
                </HStack>

                {/* Column Headers */}
                <Flex
                    px={4}
                    py={2}
                    bg="gray.100"
                    borderTopRadius="md"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    border="1px solid"
                    borderBottomWidth={0}
                >
                    <Box flex="1">Client</Box>
                    <Box w="110px">Status</Box>
                    <Box w="180px">Created</Box>
                    <Box w="40px" />
                </Flex>

                {/* List */}
                <Box bg="white" border="1px solid" borderColor="gray.200" borderBottomRadius="md" overflow="hidden">
                    {filtered.length === 0 ? (
                        <VStack py={12} color="gray.400" gap={2}>
                            <Icon as={IconBuildingStore} boxSize={8} />
                            <Text fontSize="sm">No clients found</Text>
                        </VStack>
                    ) : (
                        filtered.map((t, i) => (
                            <ClientListRow
                                key={t.id}
                                tenant={t}
                                dateFormat={user.dateFormat}
                                onEdit={() => navigate(`/admin/clients/${t.id}`)}
                                isLast={i === filtered.length - 1}
                            />
                        ))
                    )}
                </Box>
            </Box>

            <TenantCreatorDialog {...createDialog} />
            <TenantRestoreDrawer {...restoreDrawer} />
        </Box>
    );
};

interface ClientListRowProps {
    tenant: TenantResponse;
    dateFormat: string;
    onEdit: () => void;
    isLast: boolean;
}

const ClientListRow: FunctionComponent<ClientListRowProps> = ({ tenant, dateFormat, onEdit, isLast }) => {
    const color = getAvatarColor(tenant.name);
    return (
        <Flex
            px={4}
            py={3}
            align="center"
            gap={3}
            borderBottom={isLast ? undefined : "1px solid"}
            borderColor="gray.100"
            _hover={{ bg: "gray.50" }}
            transition="background 0.15s"
        >
            <Avatar size="sm" name={tenant.name} bg={`${color}.400`} color="white" />
            <Box flex="1" minW={0}>
                <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                    {tenant.name}
                </Text>
                {tenant.externalId && (
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {tenant.externalId}
                    </Text>
                )}
                {tenant.description && (
                    <Text fontSize="xs" color="gray.400" noOfLines={1}>
                        {tenant.description}
                    </Text>
                )}
            </Box>
            <Box w="110px">
                <Badge colorScheme={tenant.isEnabled ? "green" : "red"} variant="subtle" fontSize="xs">
                    {tenant.isEnabled ? "Enabled" : "Disabled"}
                </Badge>
            </Box>
            <Box w="180px">
                <Text fontSize="xs" color="gray.600">
                    {tenant.createdOn ? formatTimestampStr(tenant.createdOn, dateFormat) : "—"}
                </Text>
                {tenant.createdByDisplayName && (
                    <Text fontSize="xs" color="gray.400" noOfLines={1}>
                        by {tenant.createdByDisplayName}
                    </Text>
                )}
            </Box>
            <Menu>
                <MenuButton as={IconButton} icon={<Icon as={IconDotsVertical} boxSize={4} />} aria-label="Client actions" size="xs" variant="ghost" w="40px" />
                <MenuList minW="120px">
                    <MenuItem onClick={onEdit}>Edit</MenuItem>
                </MenuList>
            </Menu>
        </Flex>
    );
};
